import { noop } from 'lodash';
import React, {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { t } from '@lingui/macro';
import { i18n } from '@lingui/core';
import { navigate } from 'gatsby';
import { User, UserType } from '@/types/index';
import {
  getAccessTokenFromStorage,
  getRefreshTokenFromStorage,
  getUserFromStorage,
  setUserInStorage,
  unsetAccessTokenInStorage,
  unsetRefreshTokenInStorage,
  unsetUserInStorage,
} from '@/utils/auth';
import routes from '@/routes';
import { isOfType } from '@/utils/user';
import { useToast } from '@/context/Toast';
import useApi from '@/hooks/useApi';
import * as userRequests from '@/requests/user';
import { ResultStatus } from '@/types/api';

type AuthenticationContext = {
  user?: User;
  accessToken?: string;
  redirectAfterSignInPath: string;
  authenticate: (user: User, accessToken: string, refreshToken: string) => void;
  unauthenticate: (next?: string) => void;
  updateUser: (user: Partial<User>) => void;
  refresh: () => Promise<User | undefined>;
  setRedirectAfterSignInPath: (path?: string) => void;
};

const Context = createContext<AuthenticationContext>({
  authenticate: noop,
  unauthenticate: noop,
  updateUser: noop,
  setRedirectAfterSignInPath: noop,
  refresh: async () => undefined,
  redirectAfterSignInPath: '/',
});

export const useAuthentication = () => useContext(Context);

type Props = PropsWithChildren<{
  defaultRedirectAfterSignInPath?: string;
}>;

let refreshing = false;

const Authentication: FC<Props> = ({
  children,
  defaultRedirectAfterSignInPath = '/',
}) => {
  const [user, setUser] = useState(getUserFromStorage());

  const [redirectAfterSignInPath, setRedirectAfterSignInPath] = useState<string>(
    defaultRedirectAfterSignInPath
  );

  const authenticate = useCallback(
    (user: User) => {
      setUserInStorage(user);
      setUser(user);
    },
    [setUser]
  );

  const { request } = useApi(userRequests.refreshToken);

  const refresh = useCallback(async () => {
    const accessToken = getAccessTokenFromStorage();
    const refreshToken = getRefreshTokenFromStorage();
    if (accessToken && refreshToken && !refreshing) {
      refreshing = true;
      const result = await request({ accessToken, refreshToken });
      if (result?.status === ResultStatus.Success && result.data) {
        authenticate(result.data.user);
        return result.data.user;
      } else {
        unauthenticate();
      }
      refreshing = false;
    }
  }, []);

  useEffect(() => {
    const accessToken = getAccessTokenFromStorage();
    if (accessToken) {
      refresh();
    }
  }, []);

  const unauthenticate = useCallback(
    (next?: string) => {
      if (next) {
        navigate(next);
      }
      unsetUserInStorage();
      unsetAccessTokenInStorage();
      unsetRefreshTokenInStorage();
      setUser(undefined);
    },
    [setUser]
  );

  const updateUser = useCallback(
    (update: Partial<User>) => {
      if (user) {
        const updatedUser = { ...user, ...update } as User;
        setUser(updatedUser);
        setUserInStorage(updatedUser);
      }
    },
    [user]
  );

  return (
    <Context.Provider
      value={{
        user,
        redirectAfterSignInPath,
        authenticate,
        unauthenticate,
        updateUser,
        refresh,
        setRedirectAfterSignInPath: useCallback(
          (path) => (path ? setRedirectAfterSignInPath(path) : noop()),
          []
        ),
      }}
    >
      {children}
    </Context.Provider>
  );
};

export const useAuthenticationGuard = (currentPath: string, userTypes?: UserType[]) => {
  const [isAllowed, setIsAllowed] = useState(false);
  const { user, refresh, setRedirectAfterSignInPath } = useAuthentication();
  const { showError } = useToast();

  const isValidUser = useCallback(
    (user: User) => {
      return !userTypes || userTypes.some((userType) => isOfType(user, userType));
    },
    [userTypes]
  );

  const check = useCallback(async () => {
    if (!user) {
      setRedirectAfterSignInPath(currentPath);
      showError({
        text: i18n._(t`You don't have permission to access this page`),
        subtext: i18n._(t`Please log in to continue`),
      });
      navigate(routes.login());
    } else if (userTypes && !isValidUser(user)) {
      const updatedUser = await refresh();
      if (!updatedUser || !isValidUser(updatedUser)) {
        showError({
          text: i18n._(t`You don't have permission to access this page`),
          subtext: i18n._(t`Please upgrade your account to continue`),
          link: [routes.upgradeAccount({ to: userTypes[0] }), i18n._(t`Upgrade account`)],
        });
        navigate(routes.profile());
      }
    } else {
      setIsAllowed(true);
    }
  }, []);

  useEffect(() => {
    check();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  return isAllowed;
};

export const useUnauthenticationGuard = (next = routes.profile()) => {
  const { user } = useAuthentication();

  useEffect(() => {
    if (user) {
      navigate(next);
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps
};

export default Authentication;
